import Service, { service } from '@ember/service';
import type { Kpi, KpiResponse } from 'walden-fast/types/fast-backend/kpi';
import type {
  Stat,
  TrendDirection,
  TrendTimeframe,
} from 'walden-fast/types/stats';
import UserService from 'walden-fast/services/user';
import ImpersonateService from 'walden-fast/services/impersonate';
import type CacheManagerService from './cache-manager';
import config from 'walden-fast/config/environment';
import type FastApiService from './fast-api';

export default class KpiService extends Service {
  @service('fast-api') declare fastApiService: FastApiService;
  @service('impersonate') declare impersonateService: ImpersonateService;
  @service('cache-manager') declare cacheManager: CacheManagerService;
  @service('user') declare userService: UserService;

  kpis: Kpi[] = [];
  private readonly CACHE_KEY = 'kpi-data';
  private readonly CACHE_DURATION = config.kpisCacheDurationMinutes * 60 * 1000;

  async fetchKpis(): Promise<Kpi[]> {
    const response = await this.fastApiService.get<KpiResponse>(
      '/kpi',
      undefined,
      {
        key: this.CACHE_KEY,
        options: {
          duration: this.CACHE_DURATION,
          useImpersonationKey: true,
        },
      },
    );
    if (!response?.kpIs) {
      throw new Error(`No KPI's fetched`);
    }

    const sortedKpis = response.kpIs.slice().sort((a, b) => {
      if (a.tileOrder === null && b.tileOrder === null) return 0;
      if (a.tileOrder === null) return 1;
      if (b.tileOrder === null) return -1;
      return a.tileOrder - b.tileOrder;
    });

    this.kpis = sortedKpis;
    return this.kpis;
  }

  resetKPIs() {
    this.kpis = [];
    // Clear cache for current impersonation context
    this.cacheManager.clear(this.CACHE_KEY);
  }

  normalizeString(str: string): string {
    return str
      .toLowerCase()
      .replace(/walden/g, '')
      .replace(/[^a-z0-9]/g, '');
  }

  getKpisForPage(page: string): Kpi[] {
    const normalizedPage = this.normalizeString(page);
    return this.kpis.filter((kpi) => {
      if (!kpi.usedInReport) return false;
      const normalizedReport = this.normalizeString(kpi.usedInReport);
      // Quickfix: Special handling for "Order 2 Cash" / "OTC" mismatch.
      if (normalizedPage === 'otc' && normalizedReport === 'order2cash')
        return true;
      return normalizedReport === normalizedPage;
    });
  }

  convertKpiToStat(kpi: Kpi): Stat {
    const trendTimeframeSplit = kpi.trend?.split('|');
    const trendDirection = trendTimeframeSplit![0];
    const trendTimeframe = trendTimeframeSplit![1];

    const trendDirectionLower = trendDirection
      ? trendDirection.toLocaleLowerCase()
      : undefined;
    const direction: TrendDirection | undefined =
      trendDirectionLower &&
      (trendDirectionLower === 'increase' ||
        trendDirectionLower === 'decrease' ||
        trendDirectionLower === 'stable')
        ? (trendDirectionLower as TrendDirection)
        : undefined;

    const timeframe: TrendTimeframe | undefined =
      trendTimeframe &&
      (trendTimeframe === 'WoW' ||
        trendTimeframe === 'MoM' ||
        trendTimeframe === 'YoY')
        ? (trendTimeframe as TrendTimeframe)
        : undefined;

    return {
      label: kpi.kpiName ?? 'N/A',
      value: kpi.value !== null ? kpi.value.toString() : 'N/A',
      unit: kpi.unit ?? undefined,
      trend:
        direction && timeframe && kpi.trendPercentage !== null
          ? {
              percentage: kpi.trendPercentage / 100,
              direction,
              timeframe,
            }
          : undefined,
    };
  }

  async getStatsForPage(page: string): Promise<Stat[]> {
    const user = await this.userService.getUser();

    if (
      (user.role === 'WaldenAdministrator' || user.role === 'WaldenUser') &&
      !this.impersonateService.impersonationAccount
    ) {
      return [];
    }

    await this.fetchKpis();

    const stats = this.getKpisForPage(page).map((kpi) =>
      this.convertKpiToStat(kpi),
    );

    return stats ?? [];
  }
}

// Don't remove this declaration: this is what enables TypeScript to resolve
// this service using `Owner.lookup('service:kpi')`, as well
// as to check when you pass the service name as an argument to the decorator,
// like `@service('kpi') declare altName: KpiService;`.
declare module '@ember/service' {
  interface Registry {
    kpi: KpiService;
  }
}
